// iFotoLife — Content EN (English)
import { IMG } from "./images";

const ui = {
  langLabel: "Language",
  backToMenu: "Menu",
  presenter: "Presenter",
  presenterNotes: "Presenter notes",
  fullscreen: "Fullscreen",
  exitFullscreen: "Exit",
  exportPdf: "Export PDF",
  share: "Share",
  linkCopied: "Link copied to clipboard",
  shareTitle: "Share this slide",
  shareText: "Personalized preview for WhatsApp & LinkedIn. The link opens this exact slide.",
  copyLink: "Copy link",
  slide: "Slide",
  of: "of",
  next: "Next",
  prev: "Previous",
  start: "Explore",
  hint: "Use ← → to navigate · F fullscreen · N notes",
  print: "Print",
};

const home = {
  eyebrow: "iFotoLife · Hospitality photo marketplace",
  title: "Your memories,\nfound at a glance.",
  intro:
    "The commercial presentation and user tutorial of iFotoLife — the premium photo experience of the finest addresses. Choose a journey below.",
  marquee: "The premium photo experience of the finest addresses —",
  cardsTitle: "Four journeys",
  cards: [
    { key: "short", index: "01", title: "Sales pitch — Short", meta: "10 slides · 5 min", desc: "The essential value proposition, condensed for a quick read." },
    { key: "full", index: "02", title: "Sales pitch — Full", meta: "14 slides · 12 min", desc: "The detailed deck: problem, solution, market, model and vision." },
    { key: "tutorial", index: "03", title: "User tutorial", meta: "8 steps", desc: "The customer experience, step by step, from search to HD download." },
    { key: "onepager", index: "04", title: "Commercial one-pager", meta: "Printable summary", desc: "The shareable, print-ready recap on a single screen." },
  ],
  taglines: [
    "The premium photo experience of the finest addresses.",
    "From captured moment to delivered memory.",
    "The photo marketplace that turns stays into revenue.",
  ],
};

const modeMeta = {
  short: "Sales pitch — Short",
  full: "Sales pitch — Full",
  tutorial: "User tutorial",
};

const s_title = {
  type: "title", accent: "gold",
  overline: "Photo marketplace · Premium hospitality",
  title: "Your memories,\nfound at a glance.",
  lead: "iFotoLife turns every stay into a delivered memory — facial recognition, personalized gallery, secure payment.",
  image: IMG.familyBeach, imageAlt: "Smiling family on a tropical beach",
  notes: "Open on emotion: the memory. In one sentence, iFotoLife instantly finds a guest's photos and delivers them. Position the brand as premium and hospitality from the first second.",
};

const s_problem_client = {
  type: "problem", accent: "coral",
  overline: "The problem · Guest side",
  title: "The finest moments\nget lost.",
  body: "During a stay, dozens of professional photos are taken — yet guests never find them, or give up in the face of a tedious process.",
  bullets: [
    { icon: "SearchX", title: "Photos untraceable", text: "No way to know which shots you appear in, among thousands." },
    { icon: "Clock", title: "Slow process", text: "Waiting, emails, USB keys: the experience breaks the magic." },
    { icon: "ImageOff", title: "Lost memories", text: "Without easy access, most photos are never purchased." },
  ],
  image: IMG.familyBeach, imageAlt: "Family vacation moment",
  notes: "Stress the frustration: the guest lives a magical moment, but the memory is lost in a dated process. Emotional AND commercial value wasted.",
};

const s_problem_hotels = {
  type: "problem", accent: "coral",
  overline: "The problem · Hotels & photographers",
  title: "An untapped\nrevenue potential.",
  body: "Venues and photographers own high-value content, but lack a tool to monetize it simply and at scale.",
  bullets: [
    { icon: "Building2", title: "Hotels without leverage", text: "No structured channel to turn photos into additional revenue." },
    { icon: "Camera", title: "Limited photographers", text: "Artisanal sales, heavy logistics, low conversion." },
    { icon: "TrendingDown", title: "Margin dissipated", text: "A premium asset stays unmonetized, stay after stay." },
  ],
  image: IMG.photographer, imageAlt: "Professional photographer at an event",
  notes: "Show the problem is also economic. Hotels and photographers own the asset but not the infrastructure. iFotoLife is that infrastructure.",
};

const s_solution = {
  type: "solution", accent: "gold",
  overline: "The solution",
  title: "iFotoLife:\nthe bridge from moment\nto memory.",
  body: "A turnkey photo marketplace connecting hotels, photographers and guests. Guests recognize themselves with a selfie, discover their gallery and buy in seconds.",
  bullets: [
    { icon: "ScanFace", title: "Facial recognition", text: "Guests find their photos instantly with a simple selfie." },
    { icon: "Store", title: "Integrated marketplace", text: "Gallery, cart, payment and HD delivery in one experience." },
    { icon: "Handshake", title: "Turnkey", text: "No logistics for the hotel: iFotoLife runs the entire chain." },
  ],
  image: IMG.resortPool, imageAlt: "Resort pool at sunset",
  notes: "This is the heart of the pitch: iFotoLife solves both problems at once — emotion for guests, revenue for venues. One platform, three winners.",
};

const s_journey_client = {
  type: "steps", accent: "gold",
  overline: "Journey · End guest",
  title: "Four gestures,\none delivered memory.",
  steps: [
    { n: "1", title: "Scan the QR", text: "The guest scans the on-site code or receives a link." },
    { n: "2", title: "Take a selfie", text: "Facial recognition identifies all their photos in seconds." },
    { n: "3", title: "Discover the gallery", text: "A personalized selection, ready to browse and choose." },
    { n: "4", title: "Pay & download", text: "Secure payment, instant HD delivery, memory guaranteed." },
  ],
  mockup: "phone", mockImage: IMG.familyBeach,
  notes: "Tell this journey as a smooth story. The guest does almost nothing: technology fades behind emotion.",
};

const s_journey_photographer = {
  type: "steps", accent: "coral",
  overline: "Journey · Partner photographer",
  title: "Shoot,\nsell, get paid.",
  steps: [
    { n: "1", title: "Publish photos", text: "Fast upload, automatic facial indexing per event." },
    { n: "2", title: "Automated sales", text: "iFotoLife runs the storefront, cart and payment for them." },
    { n: "3", title: "Watermark & protection", text: "Protected preview, HD delivery only after purchase." },
    { n: "4", title: "Structured revenue", text: "Clear, automated sales tracking and payouts." },
  ],
  mockup: "desktop", mockImage: IMG.photographer,
  notes: "The photographer moves from artisanal selling to a structured channel. Stress time savings and protection of their work.",
};

const s_value_hotels = {
  type: "stats", accent: "gold",
  overline: "Value · Hotels & resorts",
  title: "Additional revenue,\nzero effort.",
  body: "iFotoLife becomes a new revenue line for the venue — no investment, no logistics, no risk.",
  stats: [
    { value: "+15%", label: "extra revenue per venue" },
    { value: "$0", label: "upfront investment" },
    { value: "< 3s", label: "to find your photos" },
    { value: "100%", label: "turnkey & hospitality brand" },
  ],
  image: IMG.hotelLobby, imageAlt: "Elegant modern hotel lobby",
  notes: "These figures are illustrative — adapt to the prospect. Key message: additional revenue, zero effort, zero risk.",
};

const s_features = {
  type: "features", accent: "gold",
  overline: "Key features",
  title: "Technology\nin service of emotion.",
  bullets: [
    { icon: "ScanFace", title: "Facial recognition", text: "Instant identification of the guest across all shots." },
    { icon: "GalleryHorizontalEnd", title: "Personalized gallery", text: "A dedicated, elegant selection, ready to buy." },
    { icon: "ShieldCheck", title: "Pro watermark", text: "Protected previews, photographer ownership preserved." },
    { icon: "CreditCard", title: "Secure payment", text: "Smooth, compliant and reassuring transaction." },
    { icon: "Download", title: "HD delivery", text: "Immediate high-definition download after purchase." },
    { icon: "QrCode", title: "QR / link access", text: "A simple entry point, on-site or remote." },
  ],
  notes: "Don't recite the list: link each feature to a benefit. Facial recognition = never lose a photo again.",
};

const s_business = {
  type: "business", accent: "gold",
  overline: "Business model",
  title: "A win-win\nmarketplace logic.",
  body: "iFotoLife runs the platform and shares the generated value between the venue, the photographer and the platform, on every sale.",
  bullets: [
    { icon: "Hotel", title: "Hotel", text: "Commission on every sale made on its premises, effortlessly." },
    { icon: "Camera", title: "Photographer", text: "Paid on sales, without managing commercial logistics." },
    { icon: "Layers", title: "iFotoLife", text: "Runs the technology, payment and customer experience." },
  ],
  stats: [
    { value: "Commission", label: "transparent per-sale model" },
    { value: "Recurring", label: "revenue on every stay" },
  ],
  notes: "The model is aligned: nobody fronts a cost, everyone wins on the sale. That's what eases hotel adoption.",
};

const s_usecases = {
  type: "usecases", accent: "gold",
  overline: "Use cases",
  title: "Wherever\nmemories are born.",
  bullets: [
    { icon: "Palmtree", title: "Resort & beach", text: "Vacation photos, sunsets, activities: the ideal ground for premium memories." },
    { icon: "Hotel", title: "Hotel & spa", text: "Business or leisure stays, exceptional moments to capture." },
    { icon: "PartyPopper", title: "Private event", text: "Weddings, seminars, celebrations: strong, concentrated photo demand." },
  ],
  image: IMG.resortAerial, imageAlt: "Aerial view of a luxury resort",
  notes: "Broaden the imagination: iFotoLife isn't just a beach tool. Adapt the use case to the prospect in front of you.",
};

const s_differentiation = {
  type: "list", accent: "gold",
  overline: "Differentiation",
  title: "Why iFotoLife\nstands out.",
  bullets: [
    { icon: "Sparkles", title: "Premium experience", text: "A hospitality standard, aligned with the finest addresses." },
    { icon: "Zap", title: "Instant", text: "Facial recognition removes the friction of manual sorting." },
    { icon: "PackageCheck", title: "Full chain", text: "From capture to HD delivery, one integrated platform." },
    { icon: "Lock", title: "Trust & security", text: "Photo protection, secure payment, controlled data." },
  ],
  notes: "Position against artisanal solutions and generic galleries: iFotoLife combines premium, instant and full chain.",
};

const s_vision = {
  type: "vision", accent: "gold",
  overline: "International vision",
  title: "The global standard\nfor stay memories.",
  body: "Multilingual by design, iFotoLife rolls out venue by venue, market by market — a network of premium destinations united by one experience.",
  stats: [
    { value: "3", label: "native languages: FR · EN · ES" },
    { value: "∞", label: "activatable destinations" },
    { value: "1", label: "experience, identical everywhere" },
  ],
  image: IMG.resortAerial, imageAlt: "Luxury destination from the sky",
  notes: "Project the ambition: iFotoLife aims to become an international standard. Native multilingual is an immediate rollout advantage.",
};

const s_faq = {
  type: "faq", accent: "gold",
  overline: "FAQ · Common objections",
  title: "Your questions,\nour answers.",
  faq: [
    { q: "Is facial recognition privacy-friendly?", a: "Yes: the selfie is only used to find your own photos, with consent and controlled data." },
    { q: "What effort for the hotel?", a: "None. iFotoLife runs the whole chain: setup, technology, payment, support." },
    { q: "How are photos protected?", a: "Watermarked previews, HD delivery only after purchase, photographer ownership preserved." },
    { q: "What does it cost the venue?", a: "Zero upfront investment: a commission model, aligned with actual sales." },
  ],
  notes: "Anticipate classic objections: privacy, effort, protection, cost. Stay factual and reassuring.",
};

const s_cta = {
  type: "cta", accent: "gold",
  overline: "Call to action",
  title: "Let's turn every stay\ninto a delivered memory.",
  body: "Join iFotoLife as a partner venue or photographer. Let's activate a new premium revenue together.",
  bullets: [
    { icon: "Mail", title: "Contact", text: "contact@ifotolife.shop" },
    { icon: "Globe", title: "Online", text: "ifotolife.shop" },
  ],
  image: IMG.resortPool, imageAlt: "Premium resort at sunset",
  notes: "End on a clear call and an emotion. Offer a concrete next step: demo, pilot activation, direct contact.",
};

const s_problem_merged = {
  type: "problem", accent: "coral",
  overline: "The problem",
  title: "Lost memories,\nuntapped revenue.",
  body: "For guests, photos get lost in a dated process. For hotels and photographers, a premium asset stays unmonetized.",
  bullets: [
    { icon: "SearchX", title: "Photos untraceable", text: "The guest never finds the shots they appear in." },
    { icon: "Building2", title: "Hotels without leverage", text: "No structured channel to turn photos into revenue." },
    { icon: "Camera", title: "Limited photographers", text: "Artisanal sales, heavy logistics, low conversion." },
  ],
  image: IMG.photographer, imageAlt: "Photographer at an event",
  notes: "Condensed version: present the dual problem (emotion + economy) in one slide, then move quickly to the solution.",
};

const s_journey_merged = {
  type: "steps", accent: "gold",
  overline: "The journeys",
  title: "Guest and photographer,\none same fluidity.",
  steps: [
    { n: "1", title: "The guest scans", text: "QR or link, then a selfie to find their photos." },
    { n: "2", title: "Gallery opens", text: "Personalized selection, payment and HD delivery." },
    { n: "3", title: "Photographer publishes", text: "Automatic upload and facial indexing." },
    { n: "4", title: "The sale happens", text: "iFotoLife handles storefront, payment and payouts." },
  ],
  mockup: "phone", mockImage: IMG.familyBeach,
  notes: "Merge of both journeys: show the symmetry of the guest and photographer experience.",
};

const full = [s_title, s_problem_client, s_problem_hotels, s_solution, s_journey_client, s_journey_photographer, s_value_hotels, s_features, s_business, s_usecases, s_differentiation, s_vision, s_faq, s_cta];
const short = [s_title, s_problem_merged, s_solution, s_journey_merged, s_value_hotels, s_features, s_business, s_usecases, s_differentiation, s_cta];

const tutorial = [
  { type: "tutorial", accent: "gold", step: "01", overline: "Step 1", title: "Welcome\nto iFotoLife.", body: "Find and download your stay's photos in seconds. Follow the guide.", mockup: "phone", mockImage: IMG.familyWatermelon, mockTitle: "iFotoLife", notes: "Reassure: the experience is simple and fast. Make them want to start." },
  { type: "tutorial", accent: "gold", step: "02", overline: "Step 2", title: "Find\nyour venue.", body: "Scan the venue's QR code or open the link you received to access your event.", mockup: "phone", mockImage: IMG.resortActivities, mockTitle: "Your stay", notes: "Show both entry points: on-site QR, or remote link." },
  { type: "tutorial", accent: "gold", step: "03", overline: "Step 3", title: "Start\nthe search.", body: "Use facial recognition to find your photos, or freely browse the event gallery.", mockup: "phone", mockImage: IMG.familyPool, mockTitle: "Search", notes: "Two options: facial recognition (fast) or gallery browsing (free)." },
  { type: "tutorial", accent: "gold", step: "04", overline: "Step 4", title: "Take\nyour selfie.", body: "A simple selfie is enough: iFotoLife instantly identifies every photo you appear in.", mockup: "phone", mockImage: IMG.familyPool, mockTitle: "Selfie", notes: "Stress simplicity: one selfie and done. Data used only for the search." },
  { type: "tutorial", accent: "gold", step: "05", overline: "Step 5", title: "Discover\nyour gallery.", body: "All your photos, gathered in a personalized gallery, ready to browse.", mockup: "phone", mockImage: IMG.familyPoolDad, mockTitle: "My gallery", notes: "A moment of delight: the guest rediscovers their memories, beautifully presented." },
  { type: "tutorial", accent: "gold", step: "06", overline: "Step 6", title: "Select\nand add to cart.", body: "Pick your favorites and add them to the cart in one gesture.", mockup: "phone", mockImage: IMG.brandPhotographer, mockTitle: "Cart", notes: "Recall the ease of selection: one tap to add, clear preview." },
  { type: "tutorial", accent: "gold", step: "07", overline: "Step 7", title: "Pay\nsecurely.", body: "Check out in seconds with a secure, reassuring payment.", mockup: "payment", mockTitle: "Payment", notes: "Reassure on payment security. Smooth, compliant transaction." },
  { type: "tutorial", accent: "gold", step: "08", overline: "Step 8", title: "Download\nyour HD photos.", body: "Your memories in high definition, available immediately. Yours to share.", mockup: "phone", mockImage: IMG.familyWatermelon, mockTitle: "Download", notes: "Close on satisfaction: the memory is delivered, in HD, right away." },
];

const onepager = {
  eyebrow: "Commercial one-pager",
  title: "iFotoLife",
  slogan: "Your memories, found at a glance.",
  intro: "iFotoLife is the premium photo marketplace for hotels, resorts and events. With facial recognition, every guest finds their photos with a selfie and downloads them in HD — generating turnkey additional revenue for the venue and the photographer.",
  problemTitle: "The problem",
  problem: "Stay photos get lost in a dated process; hotels and photographers have no tool to simply monetize this premium asset.",
  solutionTitle: "The solution",
  solution: "A turnkey platform: facial recognition, personalized gallery, pro watermark, secure payment and HD delivery. Three winners, one experience.",
  argsTitle: "5 key arguments",
  args: [
    "Instant facial recognition — never lose a photo again.",
    "Turnkey additional revenue for hotels & resorts.",
    "Structured, automated sales for photographers.",
    "Premium customer experience aligned with hospitality standards.",
    "Secure payment + HD delivery + pro watermark.",
  ],
  statsTitle: "Key figures",
  stats: [
    { value: "+15%", label: "extra revenue" },
    { value: "< 3s", label: "to find your photos" },
    { value: "$0", label: "upfront investment" },
    { value: "3", label: "languages: FR · EN · ES" },
  ],
  audienceTitle: "For whom",
  audience: ["Hotels & resorts", "Partner photographers", "Investors & partners", "End guests"],
  ctaTitle: "Let's partner up",
  cta: "Let's activate a new premium revenue for your venue together. Contact: contact@ifotolife.shop · ifotolife.shop",
};

export const en = { ui, home, modeMeta, decks: { full, short, tutorial }, onepager };
